









































































import { Component, Vue, Prop, Ref, Watch } from 'vue-property-decorator'
import { Card, Button } from '@alpha/ink'

import CompletedTest from '@/components/TestDetails/CompletedTest.vue'
import ClassicTestContent from '@/components/TestDetails/ClassicTestContent.vue'
import TemplateManager, { Location } from '@/components/Templates/TemplateManager.vue'
import EditActions from '@/components/EditActions.vue'
import EditModal from '@/components/EditModal.vue'
import store from '@/store'
import api from '@/api'
import * as db from '@shared/types/database'
import ToastAlert from '@/components/Toast.vue'
import { TemplateSubmissionPayload } from '@/utilities/template-helpers'
import { PresetTestMessages } from '@/store/modules/notification-store-module'
import sendCustomQuestionAnalytics from '@/analytics/customQuestionTracking'
import analytics from '@/analytics'
import * as ah from '@/utilities/asset-helpers'

@Component({
  components: {
    Card,
    CompletedTest,
    TemplateManager,
    ClassicTestContent,
    Button,
    EditActions,
    EditModal,
    ToastAlert
  }
})
export default class DraftOrSubmittedTest extends Vue {
  @Prop({ required: true }) readonly test!: db.Test
  @Prop({ required: true }) readonly workspaceId!: string
  @Prop({ required: false, default: '' }) readonly projectId!: string
  @Prop({ default: null }) readonly selectedTemplate!: db.Template
  @Prop({ default: false }) readonly templatesValidationError!:boolean
  @Ref('editQuestions') readonly editTemplateManagerComponent!: TemplateManager
  @Ref('questions') readonly questionsTemplateManagerComponent!: TemplateManager
  @Prop({ required: true }) isTemplated!: boolean

  areUploadsValid = true
  edit: Boolean = false
  savingClassicTest = false
  savingTemplate = false
  testPayloadUpdate: Partial<db.TestPayload> = {}
  assetKeysPendingDeletion: string[] = []
  templateSubmissionPayload: TemplateSubmissionPayload | null = null
  pendingPrototypes: db.TestPayload['prototypes'] = Object.assign({}, this.test.payload.prototypes)
  sharedPrototypes: db.TestPayload['prototypes'] = Object.assign({}, this.test.payload.prototypes)
  location = Location.DraftOrSubmittedTest
  saveSubmitted: boolean = false

  @Watch('test')
  updateInitialPrototypes () {
    // Seems like a reactivity issue but pendingPrototypes was being set with a out of date test value from the store, this forces an update to the correct value
    this.pendingPrototypes = Object.assign({}, this.test.payload.prototypes)
  }

  @Watch('edit')
  editUpdated () {
    if (!this.edit) return
    if (this.projectId) this.retrieveSharedPrototypes()

    analytics.track('Test Begin Editing', {
      testId: this.test.id
    })
  }

  get acl () {
    return store.state.user.acl?.permissions
  }

  get title () {
    return this.test.payload.title
  }

  get templateFieldValues () {
    return this.test.payload.templateFieldValues
  }

  async retrieveSharedPrototypes () {
    const prototypes = await api.getSharedPrototypes(this.workspaceId, this.projectId)
    this.sharedPrototypes = { ...ah.formatGraphPrototypes(prototypes), ...this.pendingPrototypes }
  }

  addPrototype (payload: { asset: db.Prototype, assetId: string }) {
    const { asset, assetId } = payload
    this.pendingPrototypes[assetId] = asset
    this.sharedPrototypes = Object.assign(this.sharedPrototypes, this.pendingPrototypes)
  }

  deletePrototype (payload: { assetId: string }) {
    const { assetId } = payload
    delete this.pendingPrototypes[assetId]
  }

  cancelEdit () {
    this.edit = false
    this.saveSubmitted = false
    this.assetKeysPendingDeletion = []
    this.pendingPrototypes = Object.assign({}, this.test.payload.prototypes)
  }

  setAreUploadsValid (areUploadsValid: boolean) {
    this.areUploadsValid = areUploadsValid
  }

  handleTestPayloadChanged (testPayloadUpdate: Partial<db.TestPayload>) {
    this.testPayloadUpdate = testPayloadUpdate
  }

  handleAssetDeleted (assetKey: string) {
    this.assetKeysPendingDeletion.push(assetKey)
  }

  trackCustomQuestionAnalytics (savedTemplatePayload: any) {
    if (!savedTemplatePayload) return
    const sharedAnalytics = {
      test_type: savedTemplatePayload.testType,
      test_id: savedTemplatePayload.testId,
      template_id: savedTemplatePayload.testTemplateId,
      tapId: store.state.workspace.workspace.id,
      tapName: store.state.workspace.workspace.name,
      tapType: store.state.workspace.workspace.payload.type
    }
    savedTemplatePayload.renderedTemplate.forEach((question: db.Question) => {
      if (!question.custom) return
      const questionSpecificAnalytics = {
        questionType: question.questionType
      }
      const fullAnalytics = Object.assign(
        {},
        sharedAnalytics,
        questionSpecificAnalytics
      )
      sendCustomQuestionAnalytics(fullAnalytics)
    })
  }

  async saveTemplate () {
    if (this.test.payload.status === 'In Review') {
      const validated = this.editTemplateManagerComponent.validate()
      if (!validated) {
        return
      }
    }

    this.savingTemplate = true
    const testId = this.test.id.toString()
    const { saveNewTemplate, newTemplatePayload, ...restOfPayload } = this.templateSubmissionPayload!
    const test = {
      ...restOfPayload,
      prototypes: this.pendingPrototypes
    }
    if (this.assetKeysPendingDeletion.length) {
      await store.dispatch.workspace.deleteAssets({
        workspaceId: this.workspaceId,
        testId,
        assetKeys: this.assetKeysPendingDeletion
      })
      this.assetKeysPendingDeletion = []
    }
    if (saveNewTemplate) {
      const result = await api.createTemplate(this.workspaceId, newTemplatePayload)
      test.testTemplateId = result.data
    }
    const result = await store.dispatch.workspace.updateTest({
      test,
      workspaceId: this.workspaceId,
      testId
    })
    // TODO:Harry Bring analytics back in when you can strip out existing questions
    // this.trackCustomQuestionAnalytics(result?.payload)
    analytics.track('Test Save Edits', { testId })
    if (result) {
      store.dispatch.notification.setMessage(PresetTestMessages.updateSuccess)
    } else {
      store.dispatch.notification.setMessage(PresetTestMessages.updateFail)
    }
    this.edit = false
    this.savingTemplate = false
    this.questionsTemplateManagerComponent.clearValidation()
    this.$emit('saveTemplate')
  }

  async saveClassicTest () {
    this.saveSubmitted = true
    if (!this.areUploadsValid) return null

    this.savingClassicTest = true
    const testId = this.test.id.toString()

    if (this.assetKeysPendingDeletion.length) {
      await store.dispatch.workspace.deleteAssets({
        workspaceId: this.workspaceId,
        testId,
        assetKeys: this.assetKeysPendingDeletion
      })
      this.assetKeysPendingDeletion = []
    }

    const test = await store.dispatch.workspace.updateTest({
      testId,
      test: this.testPayloadUpdate,
      workspaceId: this.workspaceId
    })
    analytics.track('Test Save Edits', { testId })

    this.savingClassicTest = false

    if (!test) {
      store.dispatch.notification.setMessage(PresetTestMessages.updateFail)
    } else {
      this.edit = false
      store.dispatch.notification.setMessage(PresetTestMessages.updateSuccess)
    }
  }
}
